// Edit planner for partitioning ops (v2 Slice E1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice E1.
//
// All six ops append a fresh statement after the source statement (DSE2-7:
// no coalescing by default). Per-engine support / rejection lives in the
// dialect synthesizer.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { DdlEdit, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAddPartition,
  synthesizeCreatePartitionFunction,
  synthesizeCreatePartitionScheme,
  synthesizeDropPartition,
  synthesizePartitionTable,
  synthesizeSwitchPartition,
} from './dialect.partitioning';

type PartitioningEdit = Extract<
  DdlEdit,
  {
    op:
      | 'partition-table'
      | 'add-partition'
      | 'drop-partition'
      | 'create-partition-function'
      | 'create-partition-scheme'
      | 'switch-partition';
  }
>;

export function planPartitioningEdit(
  ctx: DdlEditCtx,
  edit: PartitioningEdit,
): readonly PlannedByteEdit[] {
  const synth = synthForPartitioning(ctx, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: edit.op,
  }];
}

function synthForPartitioning(ctx: DdlEditCtx, edit: PartitioningEdit): string {
  switch (edit.op) {
    case 'partition-table':
      return synthesizePartitionTable(ctx.engine, edit.table, edit.spec);
    case 'add-partition':
      return synthesizeAddPartition(ctx.engine, edit.table, edit.partition);
    case 'drop-partition':
      return synthesizeDropPartition(
        ctx.engine,
        edit.table,
        edit.partitionName,
        edit.pgsqlConcurrently ?? false,
        edit.oracleUpdateGlobalIndexes ?? false,
      );
    case 'create-partition-function':
      return synthesizeCreatePartitionFunction(ctx.engine, edit.partitionFunction);
    case 'create-partition-scheme':
      return synthesizeCreatePartitionScheme(ctx.engine, edit.partitionScheme);
    case 'switch-partition':
      return synthesizeSwitchPartition(
        ctx.engine,
        edit.sourceTable,
        edit.sourcePartition,
        edit.targetTable,
        edit.targetPartition,
      );
  }
}
